// lib/takeoff/estimateCalc.js
// ONE source of truth for assembly-estimate maths. The server copy (utils/estimateMath.js) is generated
// from this file by scripts/syncEstimateMath.sh — edit here, then re-run the script.
//
// Per assembly item:
//   qty     = driver × layers × multiplier × (1 + waste%)
//   driver  = the takeoff quantity in the item's unit (SF / LF / EA) — see calcItem
//   pieces  = ceil( driver ÷ coverage )       e.g. 420 SF line ÷ 42 SF per EA = 10 pcs
//   studs   = Σ per straight wall: ceil(wall LF × 12 ÷ O.C.) + 1   (a line splits into walls where it turns)
//   hours   = qty ÷ productionRate            (labour items only)
//   amount  = qty × rate                      (pieces of an SF/LF-priced item: pieces × coverage × rate)

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
// stud counts: how far (inches) a drawn wall may run past a stud spacing before it gets another stud
export const STUD_TOL_IN = 3;

export const DEFAULT_SETTINGS = {
  overheadPct: 10,
  profitPct: 10,
  contingencyPct: 0,
  taxPct: 0,
  crewSize: 4,
  hoursPerDay: 8,
};

export const CALC_MODE_OPTIONS = [
  ["auto", "Auto"],
  ["takeoff", "Qty (item UOM)"],
  ["sf", "Square feet"],
  ["lf", "Linear feet"],
  ["ea", "Count (EA)"],
  ["pieces", "Pieces ÷ coverage"],
  ["oc", "Studs @ O.C."],
];

// ── dimension parsing ───────────────────────────────────────────────────────
// One dimension → feet.  Handles  10'  ·  10'-6"  ·  10' 6 1/2"  ·  134.5"  ·  145-1/2"  ·  145 1/2"  ·  3-5/8"
// A bare number is feet when small (4, 8, 12) and inches when large (48, 96, 145.5) — see `bare`.
function parseNumPart(str) {
  // "145-1/2" · "145 1/2" · "1/2" · "134.5" → number
  const t = String(str).trim();
  let m = t.match(/^(\d+)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  m = t.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (m) return Number(m[1]) / Number(m[2]);
  m = t.match(/^\d+(?:\.\d+)?$/);
  return m ? parseFloat(t) : null;
}
function parseDim(raw) {
  let s = String(raw ?? "")
    .toLowerCase()
    .replace(/[”″“]/g, '"')
    .replace(/[’′‘]/g, "'")
    .replace(/\bfeet\b|\bfoot\b|\bft\b/g, "'")
    .replace(/\binches\b|\binch\b|\bin\b/g, '"')
    .replace(/,/g, "")
    .trim();
  if (!s) return null;
  // feet and optional inches:  10'-6 1/2"  /  10' 6"  /  10'
  let m = s.match(/^([\d.]+)\s*'\s*-?\s*(?:([\d./\s-]+?)\s*"?)?$/);
  if (m) {
    const ft = parseFloat(m[1]);
    const inch = m[2] && m[2].trim() ? parseNumPart(m[2]) : 0;
    if (Number.isFinite(ft) && inch !== null)
      return { ft: ft + inch / 12, marked: true };
  }
  // inches only:  134.5"  /  145-1/2"
  m = s.match(/^(.+?)\s*"$/);
  if (m) {
    const inch = parseNumPart(m[1]);
    if (inch !== null) return { ft: inch / 12, marked: true };
  }
  const n = parseNumPart(s);
  if (n !== null) return { ft: n > 24 ? n / 12 : n, marked: false, bare: true };
  return null;
}

// a single dimension in feet (null when it can't be read)
export function parseFeet(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const d = parseDim(raw);
  return d ? d.ft : null;
}

// "4x8" / `4'x8'` / `48" x 96"` → 32 SF  ·  "12'" / "145-1/2\"" → LF  ·  `3-5/8" x 10'` with an LF uom → 10 LF
export function parseSize(size, uom) {
  const s = String(size || "")
    .toLowerCase()
    .replace(/×/g, "x")
    .replace(/\bby\b/g, "x")
    .trim();
  if (!s) return null;
  const parts = s.split(/\s*x\s*/).filter(Boolean);
  const dims = parts.map(parseDim);
  if (!dims.length || dims.some((d) => !d)) return null;
  if (dims.length === 1) {
    const d = dims[0];
    return d.bare && !d.marked ? null : { unit: "LF", value: d.ft };
  }
  const lfUom =
    uomUnit(uom) === "LF" || /\blf\b|lin/.test(String(uom || "").toLowerCase());
  if (lfUom) return { unit: "LF", value: Math.max(...dims.map((d) => d.ft)) };
  if (dims.length === 2) return { unit: "SF", value: dims[0].ft * dims[1].ft };
  return { unit: "LF", value: Math.max(...dims.map((d) => d.ft)) };
}

export function uomUnit(uom) {
  const u = String(uom || "")
    .toLowerCase()
    .trim();
  if (!u) return null;
  if (/^(sf|sqft|sq\.?\s?ft|square|sq)$/.test(u)) return "SF";
  if (/^(lf|lnft|lin\.?\s?ft|linear)$/.test(u)) return "LF";
  if (/^(ea|each|pc|pcs|piece|pieces|unit|ct|count)$/.test(u)) return "EA";
  return null;
}

// same rule the existing estimate used: SF / LF / sheet items are priced per coverage unit
const COV_UOMS = ["sf", "lf", "sq ft", "lin ft", "square", "linear", "sheet"];
export const isCovUom = (uom) =>
  COV_UOMS.some((u) =>
    String(uom || "")
      .toLowerCase()
      .includes(u),
  );

// coverage per piece. The SIZE decides ("4x8" → 32 SF, `145-1/2"` → 12.125 LF) so changing the size updates the
// coverage on its own; a number typed into the Coverage box (covManual) or on an item with no readable size is used instead.
export function resolveCoverage(item) {
  const fromSize = parseSize(item.size, item.uom);
  const typed = num(item.coveragePerUnit, 0);
  const u = uomUnit(item.uom);
  if (fromSize && !item.covManual)
    return { value: fromSize.value, unit: fromSize.unit, source: "size" };
  if (typed > 0) {
    const unit =
      item.covUnit === "LF" || item.covUnit === "SF"
        ? item.covUnit
        : u === "SF" || u === "LF"
          ? u
          : fromSize?.unit || "SF";
    return { value: typed, unit, source: "manual" };
  }
  if (fromSize)
    return { value: fromSize.value, unit: fromSize.unit, source: "size" };
  return null;
}

const nativeUnit = (cond) =>
  cond.type === "area" ? "SF" : cond.type === "linear" ? "LF" : "EA";

// A piece-counted row (O.C. studs, or pieces ÷ coverage) also has a TOTAL in the item's coverage unit:
//   total = pieces × coverage per piece   (e.g. 73 studs × 12.125 LF = 885 LF)
// • SF/LF-priced material: stays counted in pieces (what you order) but is priced as total × rate
// • SF/LF labour: the quantity BECOMES the total, so rate and production rate (LF/hr, SF/hr) apply to it
function applyCoverageTotal(out, item, each, unit, exact = null) {
  const pieces = out.qty;
  out.pieces = pieces;
  out.covUsed = each > 0 ? each : out.covUsed;
  out.covUnit = unit || out.covUnit;
  if (!(each > 0) || !isCovUom(item.uom)) return;
  // O.C. rows/studs: the LF is the ASSEMBLY's linear footage (rows × run, studs × height),
  // not pieces × the stick length — leftover stick is waste, not extra footage
  const total = exact != null && unit === "LF" ? exact : pieces * each;
  out.total = total;
  out.totalUnit = unit;
  if (item.category === "L") {
    out.qty = total;
    out.unit = unit;
  } else out.pricedPerCov = true;
}

export function calcItem(cond, m, item) {
  m = m || {};
  const layers = num(item.layers, 1);
  const mult = num(item.multiplier, 1);
  const waste = num(item.wastePct, 0) / 100;
  const f = layers * mult * (1 + waste);
  const cov = resolveCoverage(item);
  let mode = item.calcMode || "auto";
  if (mode === "coverage") mode = "pieces"; // legacy name
  if (mode === "auto")
    mode = item.category !== "L" && cov ? "pieces" : "takeoff";

  const driverFor = (unit) => {
    if (m.uncal) return null;
    const v = unit === "SF" ? m.sf : unit === "LF" ? m.lf : m.count;
    return v === null || v === undefined ? null : v;
  };
  const traced = (m.sf || 0) > 0 || (m.lf || 0) > 0 || (m.count || 0) > 0;
  const whyNone = (unit) =>
    m.uncal
      ? "Set sheet scale"
      : !traced
        ? "Nothing traced yet"
        : unit === "SF" && cond.type === "linear"
          ? "Needs wall height"
          : unit === "LF" && cond.type === "count"
            ? "Needs height"
            : "No quantity";

  const out = {
    mode,
    qty: null,
    unit: item.uom || "EA",
    driver: null,
    driverUnit: null,
    covUsed: cov?.value ?? null,
    covUnit: cov?.unit ?? null,
    covSource: cov?.source ?? null,
    pricedPerCov: false,
    hours: null,
    amount: null,
    note: "",
  };

  if (mode === "oc") {
    const oc = num(item.ocInches, 0);
    if (oc <= 0) {
      out.note = "Enter O.C. spacing";
      return out;
    }
    const runs =
      m.shapeLfs && m.shapeLfs.length ? m.shapeLfs : m.lf ? [m.lf] : [];
    if (!runs.length) {
      out.note = whyNone("LF");
      return out;
    }
    // HORIZONTAL (carrying channel, furring): rows stacked up the wall every `oc` inches,
    // each row running the wall's length.  rows = floor(height ÷ spacing)  (9′ @ 48″ → 2 rows — no room for a partial row)
    if (item.ocDir === "h") {
      const hh = num(cond.height, 0);
      if (hh <= 0) {
        out.note = "Needs wall height for horizontal rows";
        return out;
      }
      const rows = Math.max(1, Math.floor((hh * 12) / oc + 1e-9));
      const totalLf = rows * runs.reduce((t, x) => t + x, 0);
      const stick = cov && cov.unit === "LF" ? cov.value : 0;
      out.driver = totalLf;
      out.driverUnit = `LF (${rows} rows)`;
      out.rows = rows;
      out.unit = "LF";
      if (stick) {
        // pieces cut per run so a run's last stick isn't shared across walls
        const pcs = runs.reduce(
          (t, x) => t + Math.ceil((x * rows * f) / stick - 1e-9),
          0,
        );
        out.qty = pcs;
        out.unit = "EA";
        applyCoverageTotal(out, item, stick, "LF", totalLf * f);
      } else if (!/^(LF|FT|LN)/i.test(String(item.uom || "").trim())) {
        out.unit = item.uom || "EA";
        out.note = "Add size (stick length) to count pieces";
        out.qty = null;
      } else {
        out.qty = Math.ceil(totalLf * f - 1e-9) || 0;
        out.total = out.qty;
        out.totalUnit = "LF";
      }
    } else {
      // VERTICAL studs, per straight wall: ceil(wall LF × 12 ÷ O.C.) + 1  (each wall has its own start + end stud,
      // so a corner gets one from both walls). A drawn line is split into walls where it turns
      // (m.shapeWalls from the viewer); without that, each drawn line counts as one wall.
      const walls =
        m.shapeWalls && m.shapeWalls.length === runs.length
          ? m.shapeWalls.flatMap((w, i) => (w && w.length ? w : [runs[i]]))
          : runs;
      // A drawn wall is rarely exact — 20' traced comes out 20'-1". Up to STUD_TOL_IN over a stud spacing
      // doesn't add a stud; more than that does (a 20'-6" wall at 16" really needs one more).
      const studs = walls.reduce(
        (s, lf) =>
          s +
          (lf > 0
            ? Math.max(0, Math.ceil((lf * 12 - STUD_TOL_IN) / oc - 1e-9)) + 1
            : 0),
        0,
      );
      // the item's size / coverage is the stick length; the wall height is the condition's.
      // A stud position needs ceil(height ÷ stick) pieces (1 when the stud is as long as the wall).
      const ht = num(cond.height, 0);
      const stick = cov && cov.unit === "LF" ? cov.value : 0;
      const perPos =
        ht && stick ? Math.max(1, Math.ceil(ht / stick - 1e-9)) : 1;
      out.driver = studs;
      out.driverUnit =
        walls.length > runs.length ? `studs (${walls.length} walls)` : "studs";
      out.walls = walls.length;
      out.qty = Math.ceil(studs * perPos * f - 1e-9) || 0;
      out.unit = "EA";
      // length of each piece: its size/coverage, else (no size) the wall height
      applyCoverageTotal(
        out,
        item,
        cov ? cov.value : ht,
        cov ? cov.unit : "LF",
        ht ? studs * ht * f : null,
      );
    }
  } else if (mode === "pieces") {
    if (!cov) {
      out.note = "Add size or coverage";
      return out;
    }
    const d = driverFor(cov.unit);
    if (d === null) {
      out.note = whyNone(cov.unit);
      return out;
    }
    out.driver = d;
    out.driverUnit = cov.unit;
    out.qty = Math.ceil((d * f) / cov.value - 1e-9);
    out.unit = "EA";
    applyCoverageTotal(out, item, cov.value, cov.unit);
  } else {
    const unit =
      mode === "sf"
        ? "SF"
        : mode === "lf"
          ? "LF"
          : mode === "ea"
            ? "EA"
            : uomUnit(item.uom) || nativeUnit(cond);
    const d = driverFor(unit);
    if (d === null) {
      out.note = whyNone(unit);
      return out;
    }
    out.driver = d;
    out.driverUnit = unit;
    out.qty = d * f;
    out.unit = unit;
  }

  const rate = num(item.rate, 0);
  out.amount = round2(
    out.pricedPerCov && out.covUsed
      ? (out.total != null ? out.total : out.qty * out.covUsed) * rate
      : out.qty * rate,
  );
  const prod = num(item.productionRate, 0);
  if (item.category === "L" && prod > 0) out.hours = out.qty / prod;
  return out;
}

// every assembly's rows, the material take-list, review flags, and the price build-up
export function rollup({
  conditions = [],
  metrics = {},
  metricsByArea = null, // { [condId]: { [bidArea]: metrics } } — sheets grouped by bid area
  mappings = [],
  settings = {},
}) {
  const S = { ...DEFAULT_SETTINGS };
  for (const k of Object.keys(DEFAULT_SETTINGS))
    if (settings[k] !== undefined && settings[k] !== "")
      S[k] = num(settings[k], DEFAULT_SETTINGS[k]);

  const blocks = [];
  const issues = [];
  const take = new Map();
  const add = (sev, condId, title, detail) =>
    issues.push({ sev, condId, title, detail });

  for (const cond of conditions) {
    const mapping = mappings.find((x) => x.conditionId === cond.id) || null;
    // One block per bid area when sheets are assigned to areas (floors can have different heights);
    // otherwise a single block for the whole condition.
    const byArea = metricsByArea?.[cond.id];
    const areaKeys = byArea ? Object.keys(byArea) : [];
    let entries = [
      { key: "", area: mapping?.bidArea || "", m: metrics[cond.id] || null },
    ];
    if (areaKeys.some((k) => k !== "")) {
      const live = areaKeys.filter((k) => {
        const x = byArea[k];
        return (
          x &&
          ((x.sf || 0) > 0 || (x.lf || 0) > 0 || (x.count || 0) > 0 || x.uncal)
        );
      });
      if (live.length)
        entries = live.map((k) => ({
          key: k,
          area: k || mapping?.bidArea || "",
          m: byArea[k],
        }));
    }

    for (const { key, area, m } of entries) {
      // wall height for THIS area, exactly as set: the height the viewer reports for these runs
      // (metrics.height), else the condition's own. Never derived from SF ÷ LF — that is net of doors/windows.
      const h = num(m?.height, 0) || num(cond.height, 0);
      const c2 = h > 0 ? { ...cond, height: h } : cond;
      const rows = (mapping?.items || []).map((item) => ({
        item,
        ...calcItem(c2, m, item),
      }));
      let material = 0,
        labour = 0,
        hours = 0;
      const where = area ? ` (${area})` : "";
      for (const r of rows) {
        const isL = r.item.category === "L";
        const label = `${cond.name}${where} · ${r.item.itemCode || r.item.title}`;
        if (r.qty === null)
          add("high", cond.id, `${label}: ${r.note || "cannot calculate"}`, "");
        else {
          if (isL) {
            labour += r.amount || 0;
            hours += r.hours || 0;
          } else {
            material += r.amount || 0;
            const tk = `${r.item.itemCode}||${r.item.size || ""}||${r.unit}`;
            const t = take.get(tk) || {
              itemCode: r.item.itemCode,
              title: r.item.title,
              size: r.item.size || "",
              unit: r.unit,
              qty: 0,
              amount: 0,
              assemblies: new Set(),
            };
            t.qty += r.qty;
            t.amount += r.amount || 0;
            t.assemblies.add(cond.name + where);
            take.set(tk, t);
          }
          if (!(num(r.item.rate) > 0) && r.qty > 0)
            add("med", cond.id, `${label}: no rate`, "Priced at $0");
          if (isL && !(num(r.item.productionRate) > 0))
            add(
              "info",
              cond.id,
              `${label}: no production rate`,
              "Hours not counted",
            );
        }
      }
      const hasQty =
        m && ((m.sf || 0) > 0 || (m.lf || 0) > 0 || (m.count || 0) > 0);
      if (m?.uncal && m.count > 0)
        add("high", cond.id, `${cond.name}${where}: sheet has no scale`, "");
      else if (!rows.length && hasQty)
        add(
          "med",
          cond.id,
          `${cond.name}${where} has takeoff but no priced items`,
          "",
        );
      blocks.push({
        key: `${cond.id}::${key}`,
        cond,
        mapping,
        area,
        height: h > 0 ? h : 0,
        heights: m?.heights || [],
        metrics: m,
        rows,
        material,
        labour,
        hours,
        total: material + labour,
      });
    }
  }

  const material = blocks.reduce((s, b) => s + b.material, 0);
  const labour = blocks.reduce((s, b) => s + b.labour, 0);
  const hours = blocks.reduce((s, b) => s + b.hours, 0);
  const direct = material + labour;
  const overhead = direct * (S.overheadPct / 100);
  const profit = (direct + overhead) * (S.profitPct / 100);
  const contingency = direct * (S.contingencyPct / 100);
  const subtotal = direct + overhead + profit + contingency;
  const tax = subtotal * (S.taxPct / 100);
  const price = subtotal + tax;
  const days =
    hours > 0 && S.crewSize > 0 && S.hoursPerDay > 0
      ? hours / (S.crewSize * S.hoursPerDay)
      : 0;

  const areaMap = new Map();
  for (const b of blocks) {
    const k = b.area || "Unassigned";
    const a = areaMap.get(k) || {
      area: k,
      material: 0,
      labour: 0,
      hours: 0,
      total: 0,
    };
    a.material += b.material;
    a.labour += b.labour;
    a.hours += b.hours;
    a.total += b.total;
    areaMap.set(k, a);
  }
  const rank = { high: 0, med: 1, info: 2 };
  issues.sort((a, b) => rank[a.sev] - rank[b.sev]);

  return {
    settings: S,
    blocks,
    byArea: [...areaMap.values()],
    issues,
    takeList: [...take.values()]
      .map((t) => ({ ...t, assemblies: [...t.assemblies] }))
      .sort((a, b) => b.amount - a.amount),
    totals: {
      material,
      labour,
      direct,
      overhead,
      profit,
      contingency,
      subtotal,
      tax,
      price,
      hours,
      days,
    },
  };
}

// ── catalog helpers ──────────────────────────────────────────────────────────
// labour → billed rate · material → default vendor's price, else default cost
export function resolveDefaultRate(dbItem) {
  if (!dbItem) return 0;
  if (dbItem.category === "L")
    return num(dbItem.defaultUnitRate, 0) || num(dbItem.defaultCost, 0);
  const dv = dbItem.defaultVendor?._id || dbItem.defaultVendor;
  const vp =
    (dbItem.vendorPrices || []).find(
      (v) => String(v.vendor?._id || v.vendor) === String(dv),
    ) || (dbItem.vendorPrices || [])[0];
  return num(vp?.price, 0) || num(dbItem.defaultCost, 0);
}

export function defaultCalcMode(cond, dbItem) {
  if (dbItem.category === "L") return "takeoff";
  const hasCov = num(dbItem.coveragePerUnit, 0) > 0 || !!parseSize(dbItem.size);
  return hasCov ? "pieces" : "takeoff";
}

// a calculated row → the item shape ChangeOrder3 parseItem() expects
export function toCoItem(cond, row, extra = {}) {
  const it = row.item;
  const isL = it.category === "L";
  return {
    itemCode: it.itemCode,
    category: isL ? "L" : "M",
    sectionNumber: it.sectionNumber || "",
    bidArea: extra.bidArea || "",
    costCodeDescription: it.title,
    itemDescription: it.title,
    size: it.size || "",
    uom: it.uom || "",
    deltaQty: round2(row.qty),
    unitCost: isL ? 0 : num(it.rate),
    unitRate: isL ? num(it.rate) : 0,
    coveragePerUnit: row.pricedPerCov
      ? row.total != null && row.qty
        ? row.total / row.qty
        : row.covUsed
      : null,
    itemDatabaseId: it.itemDatabaseId || null,
    breakdownDescription: `${cond.name} — ${it.title}`,
  };
}
